import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { Item, Project, PromptTemplate } from "../lib/types.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const textareaCls = `${inputCls} font-mono text-xs`;

const btnPrimary =
  "rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const btnGhost =
  "rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 " +
  "dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

interface AssembledPrompt {
  system: string;
  user: string;
  warnings: string[];
}

/** Edits system/user/outputSchema/variantHints for one template (global or a
 * project override) and offers a "preview against item" panel that calls the
 * server's real prompt assembler so what you see is what generation will send. */
export function TemplateEditor({
  template,
  projectId,
  onSaved,
}: {
  template: PromptTemplate;
  projectId?: string;
  onSaved: (t: PromptTemplate) => void;
}) {
  const [system, setSystem] = useState(template.system);
  const [user, setUser] = useState(template.user);
  const [schemaText, setSchemaText] = useState(
    template.outputSchema ? JSON.stringify(template.outputSchema, null, 2) : ""
  );
  const [variantHints, setVariantHints] = useState(template.variantHints.join("\n"));
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [previewProjectId, setPreviewProjectId] = useState(projectId ?? "");
  const [items, setItems] = useState<Item[]>([]);
  const [previewItemId, setPreviewItemId] = useState("");
  const [assembled, setAssembled] = useState<AssembledPrompt | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    setSystem(template.system);
    setUser(template.user);
    setSchemaText(template.outputSchema ? JSON.stringify(template.outputSchema, null, 2) : "");
    setVariantHints(template.variantHints.join("\n"));
    setSchemaError(null);
    setSaveError(null);
    setAssembled(null);
  }, [template]);

  useEffect(() => {
    if (!previewOpen || projects.length > 0) return;
    void api<{ items: Project[] }>("/api/projects").then((res) => setProjects(res.items));
  }, [previewOpen, projects.length]);

  useEffect(() => {
    if (!previewProjectId) {
      setItems([]);
      return;
    }
    void api<{ items: Item[] }>(`/api/projects/${previewProjectId}/items`).then((res) => setItems(res.items));
  }, [previewProjectId]);

  async function handleSave() {
    let outputSchema: Record<string, unknown> | undefined;
    if (schemaText.trim()) {
      try {
        outputSchema = JSON.parse(schemaText) as Record<string, unknown>;
        setSchemaError(null);
      } catch {
        setSchemaError("Output schema must be valid JSON.");
        return;
      }
    }
    setBusy(true);
    setSaveError(null);
    try {
      const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
      const updated = await api<PromptTemplate>(`/api/templates/${template.templateId}${query}`, {
        method: "PATCH",
        json: { system, user, outputSchema, variantHints: variantHints.split("\n").map((h) => h.trim()) },
      });
      onSaved(updated);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save the template.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePreview() {
    if (!previewProjectId || !previewItemId) return;
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const result = await api<AssembledPrompt>(`/api/templates/${template.templateId}/preview`, {
        method: "POST",
        json: { projectId: previewProjectId, itemId: previewItemId },
      });
      setAssembled(result);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Could not assemble the preview.");
    } finally {
      setPreviewBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">System prompt</label>
        <textarea rows={3} className={textareaCls} value={system} onChange={(e) => setSystem(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">User prompt</label>
        <textarea rows={8} className={textareaCls} value={user} onChange={(e) => setUser(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Output schema (JSON, optional)
        </label>
        <textarea
          rows={6}
          className={textareaCls}
          value={schemaText}
          onChange={(e) => setSchemaText(e.target.value)}
        />
        {schemaError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{schemaError}</p>}
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Variant hints (one per line)
        </label>
        <textarea
          rows={4}
          className={textareaCls}
          value={variantHints}
          onChange={(e) => setVariantHints(e.target.value)}
        />
      </div>

      {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}
      <div className="flex gap-2">
        <button type="button" className={btnPrimary} disabled={busy} onClick={() => void handleSave()}>
          {busy ? "Saving..." : "Save"}
        </button>
        <button type="button" className={btnGhost} onClick={() => setPreviewOpen((v) => !v)}>
          {previewOpen ? "Hide preview" : "Preview against item"}
        </button>
      </div>

      {previewOpen && (
        <div className="rounded-md border border-gray-200 p-4 dark:border-gray-800">
          <div className="flex flex-wrap gap-2">
            <select
              className={inputCls}
              value={previewProjectId}
              onChange={(e) => {
                setPreviewProjectId(e.target.value);
                setPreviewItemId("");
              }}
            >
              <option value="">Select a project...</option>
              {projects.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              className={inputCls}
              value={previewItemId}
              onChange={(e) => setPreviewItemId(e.target.value)}
              disabled={!previewProjectId}
            >
              <option value="">Select an item...</option>
              {items.map((i) => (
                <option key={i.itemId} value={i.itemId}>
                  {i.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={btnGhost}
              disabled={!previewProjectId || !previewItemId || previewBusy}
              onClick={() => void handlePreview()}
            >
              {previewBusy ? "Assembling..." : "Assemble"}
            </button>
          </div>
          {previewError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{previewError}</p>}
          {assembled && (
            <div className="mt-3 space-y-2 text-xs">
              {assembled.warnings.length > 0 && (
                <p className="text-amber-600 dark:text-amber-400">{assembled.warnings.join("; ")}</p>
              )}
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-300">System</div>
                <pre className="whitespace-pre-wrap rounded bg-gray-50 p-2 font-mono dark:bg-gray-800">
                  {assembled.system || "(empty)"}
                </pre>
              </div>
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-300">User</div>
                <pre className="whitespace-pre-wrap rounded bg-gray-50 p-2 font-mono dark:bg-gray-800">
                  {assembled.user}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
